import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { HeroPromptSection } from './features/landing/HeroPromptSection';
import { LandingInfoPage } from './features/landing/LandingInfoPage';
import { PricingPage } from './features/pricing/PricingPage';
import { AppInstancePage } from './pages/apps/AppInstancePage';
import { CloudPage } from './pages/cloud/CloudPage';
import { HomePage } from './pages/home/HomePage';
import { LoginPage } from './pages/login/LoginPage';
import { OtpPage } from './pages/login/OtpPage';
import { ChatPage } from './pages/chat/ChatPage';
import { SharedConversationPage } from './pages/share/SharedConversationPage';
import { ImagesPage } from './pages/images/ImagesPage';
import { ModelPickerPage } from './pages/models/ModelPickerPage';
import { ProjectEditPage } from './pages/projects/ProjectEditPage';
import { ProjectsPage } from './pages/projects/ProjectsPage';
import { FolderDetailPage } from './pages/folders/FolderDetailPage';
import { FoldersPage } from './pages/folders/FoldersPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { DeploymentsPage } from './pages/workspace/DeploymentsPage';
import { UsersPage } from './pages/workspace/UsersPage';
import { RequestsPage } from './pages/workspace/RequestsPage';
import { FormSubmissionsPage } from './pages/workspace/FormSubmissionsPage';
import { SkillsPage } from './pages/workspace/SkillsPage';
import { WorkspacePage } from './pages/workspace/WorkspacePage';
import { useLanguageFromUrl } from './hooks/useLanguageFromUrl';
import { useAppSelector } from './hooks/useAppRedux';
import { selectDirection, selectTheme } from './redux/slices/appSlice';
import { SettingsModalProvider } from './features/settings/SettingsModalContext';
import { SettingsModal } from './features/settings/SettingsModal';

function LanguageSync() {
  useLanguageFromUrl();
  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAppSelector((state: any) => state.auth.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAppSelector((state: any) => state.auth.isAuthenticated);
  const location = useLocation();
  const state = location.state as any;
  const redirectState = state?.redirectState || {
    openCreateProject: state?.openCreateProject,
    projectDraft: state?.projectDraft,
    openPublicTemplateId: state?.openPublicTemplateId
  };

  return isAuthenticated ? <Navigate to={state?.redirectTo || '/home'} replace state={redirectState} /> : <>{children}</>;
}

function App() {
  const direction = useAppSelector(selectDirection);
  const theme = useAppSelector(selectTheme);
  const isAuthenticated = useAppSelector((state: any) => state.auth.isAuthenticated);

  useEffect(() => {
    const resolved =
      theme === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : theme;
    document.documentElement.dir = direction;
    document.documentElement.lang = direction === 'rtl' ? 'fa' : 'en';
    document.documentElement.classList.toggle('dark', resolved === 'dark');

    if (theme !== 'system') {
      return undefined;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      document.documentElement.classList.toggle('dark', media.matches);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [direction, theme]);

  return (
    <div className="min-h-screen bg-app text-slate-900 transition-colors duration-300 dark:text-slate-100">
      <LanguageSync />
      <SettingsModalProvider>
        <SettingsModal />
        <Routes>
        <Route path="/" element={<HeroPromptSection />} />
        <Route path="/about" element={<LandingInfoPage type="about" />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/docs" element={<LandingInfoPage type="docs" />} />
        <Route path="/share/:token" element={<SharedConversationPage />} />
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/login/otp"
          element={
            <PublicOnlyRoute>
              <OtpPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/models/:kind"
          element={
            <ProtectedRoute>
              <ModelPickerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:conversationId"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/images"
          element={
            <ProtectedRoute>
              <ImagesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/images/:sessionId"
          element={
            <ProtectedRoute>
              <ImagesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/folders"
          element={
            <ProtectedRoute>
              <FoldersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/folders/:folderId"
          element={
            <ProtectedRoute>
              <FolderDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/apps"
          element={
            <ProtectedRoute>
              <Navigate to="/cloud" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/apps/:instanceId"
          element={
            <ProtectedRoute>
              <AppInstancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cloud"
          element={
            <ProtectedRoute>
              <CloudPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/edit"
          element={
            <ProtectedRoute>
              <ProjectEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/:projectId"
          element={
            <ProtectedRoute>
              <WorkspacePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/:projectId/deployments"
          element={
            <ProtectedRoute>
              <DeploymentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/:projectId/users"
          element={
            <ProtectedRoute>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/:projectId/skills"
          element={
            <ProtectedRoute>
              <SkillsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/:projectId/requests"
          element={
            <ProtectedRoute>
              <RequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/:projectId/requests/:formId"
          element={
            <ProtectedRoute>
              <FormSubmissionsPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to={isAuthenticated ? '/home' : '/'} replace />} />
      </Routes>
      </SettingsModalProvider>
    </div>
  );
}

export default App;
